import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  TemplateRef,
  computed,
  contentChild,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FileHandle } from '../../drag-drop-files.directive';
import { PanFilepickerPreviewContext } from '../../types';

@Component({
  selector: 'app-pan-filepicker',
  templateUrl: './pan-filepicker.component.html',
  styleUrl: './pan-filepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PanFilepickerComponent {
  public accept = input<string[]>(['.jpg', '.jpeg', '.png', '.webp']);
  public multiple = input<boolean>(true);
  // Invariant: must be exactly the URLs the consumer derived from the most recent
  // selected emission, in the same order, with no other URLs interleaved (see onRemove).
  public previewUrls = input<string[]>([]);
  public invalid = input<boolean>(false);

  public selected = output<File[]>();
  public remove = output<string>();

  public validationError = signal('');
  public dragActive = signal(false);

  private fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  public previewTemplate = contentChild<TemplateRef<PanFilepickerPreviewContext>>(TemplateRef);
  private files: File[] = [];

  public acceptAttr = computed(() => this.accept().join(','));
  public acceptLabel = computed(() => {
    const exts = this.accept().map((ext) => ext.replace(/^\./, ''));
    return exts.filter((ext) => !(ext === 'jpeg' && exts.includes('jpg'))).join(', ');
  });
  public dropzoneClasses = computed(() => [
    'border-2',
    'border-dashed',
    'rounded-lg',
    'p-4',
    'bg-gray-50',
    'hover:bg-gray-100',
    'focus:outline-none',
    this.invalid() ? 'border-danger' : this.dragActive() ? 'border-primary' : 'border-default',
  ]);

  public dropzoneAriaLabel = computed(
    () => `File picker dropzone. Drop or paste files, or open the picker. Accepted formats: ${this.acceptLabel()}.`,
  );
  public dropzoneAriaDescribedBy = computed(() => {
    const ids = ['filepicker-instructions'];
    if (this.validationError()) {
      ids.push('filepicker-validation-error');
    }
    return ids.join(' ');
  });

  public openPicker(): void {
    this.fileInput()?.nativeElement.click();
  }

  public onDropzoneKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.openPicker();
  }

  public onPick(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    this.addFiles(Array.from(target.files));
    target.value = '';
  }

  public onDrop(handles: FileHandle[]): void {
    this.addFiles(handles.map((handle) => handle.file));
  }

  public onPaste(files: File[]): void {
    this.addFiles(files);
  }

  public previewContext(url: string): PanFilepickerPreviewContext {
    return { $implicit: url, remove: () => this.onRemove(url) };
  }

  public clear(): void {
    this.files = [];
    this.validationError.set('');
    const input = this.fileInput();
    if (input) input.nativeElement.value = '';
    this.selected.emit(this.files);
  }

  private onRemove(url: string): void {
    const index = this.previewUrls().indexOf(url);
    if (index !== -1) this.files.splice(index, 1);
    this.remove.emit(url);
  }

  private addFiles(files: File[]): void {
    this.validationError.set('');
    const invalidFile = files.find((file) => !this.accept().some((ext) => file.name.toLowerCase().endsWith(ext)));
    if (invalidFile) {
      this.validationError.set(`Only ${this.accept().join(', ')} files are allowed.`);
      return;
    }
    this.files = [...this.files, ...files];
    this.selected.emit(this.files);
  }
}
